import { XMLParser, XMLBuilder, XMLValidator } from "fast-xml-parser";
import {
  presetTarget,
  isDynamicChoiceField,
  statementSelectionSchema,
  type Field,
} from "@proof/core";
type Records = Field["presets"];
type Node = Record<string, unknown>;
const name = (node: Node) =>
  Object.keys(node).find(
    (key) => key !== ":@" && !key.startsWith("#") && !key.startsWith("?"),
  );
const children = (node: Node) => {
  const key = name(node);
  return key && Array.isArray(node[key]) ? (node[key] as Node[]) : [];
};
const cell = (nodes: Node[]) => {
  if (nodes.some((node) => name(node)))
    throw new Error("PREPOPULATION_NESTED_CELL");
  const value = nodes
    .map((node) => (typeof node["#text"] === "string" ? node["#text"] : ""))
    .join("");
  if (value.length > 32768) throw new Error("PREPOPULATION_VALUE_LIMIT");
  return value;
};
export function parsePrepopulationXml(xml: string): Records {
  if (
    typeof xml !== "string" ||
    new TextEncoder().encode(xml).length > 2000000 ||
    (xml.match(/</g)?.length || 0) > 30000
  )
    throw new Error("PREPOPULATION_XML_LIMIT");
  if (/<!\s*(?:DOCTYPE|ENTITY)/i.test(xml))
    throw new Error("PREPOPULATION_XML_DECLARATION");
  if (XMLValidator.validate(xml) !== true)
    throw new Error("PREPOPULATION_XML_INVALID");
  let nodes: Node[];
  try {
    nodes = new XMLParser({
      preserveOrder: true,
      ignoreAttributes: false,
      parseTagValue: false,
      trimValues: false,
      maxNestedTags: 8,
    }).parse(xml);
  } catch {
    throw new Error("PREPOPULATION_XML_INVALID");
  }
  const root = nodes.find((node) => name(node));
  if (!root || name(root) !== "prepopulatedataList")
    throw new Error("PREPOPULATION_ROOT_INVALID");
  const records: Records = [],
    ids = new Set<string>();
  for (const row of children(root)) {
    if (!name(row)) continue;
    if (name(row) !== "prepopulatedata")
      throw new Error("PREPOPULATION_ROW_INVALID");
    const entries: [string, string][] = [],
      keys = new Set<string>();
    for (const column of children(row)) {
      const key = name(column);
      if (!key) continue;
      if (keys.has(key)) throw new Error("PREPOPULATION_DUPLICATE_COLUMN");
      if (key.length > 200) throw new Error("PREPOPULATION_KEY_LIMIT");
      keys.add(key);
      entries.push([key, cell(children(column))]);
      if (keys.size > 1002) throw new Error("PREPOPULATION_TARGET_LIMIT");
    }
    const data = Object.fromEntries(entries),
      id = data._pre_pop_id,
      label = data.value;
    const values = Object.fromEntries(
      entries.filter(([key]) => !["_pre_pop_id", "value"].includes(key)),
    );
    if (!label?.trim() || !Object.keys(values).length) continue;
    if (!id || id.length > 200)
      throw new Error("PREPOPULATION_OPTION_ID_REQUIRED");
    if (ids.has(id)) throw new Error("PREPOPULATION_DUPLICATE_OPTION");
    ids.add(id);
    records.push({ id, label, values });
    if (records.length > 1000) throw new Error("PREPOPULATION_OPTION_LIMIT");
  }
  return records.sort((a, b) =>
    a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
  );
}
/** Unknown columns remain in the opaque row; native assignments bind to stable field identities. */
export function bindPrepopulation(
  records: Records,
  fields: readonly Field[],
  selectorId?: string,
  assets: Readonly<Record<string, string>> = {},
) {
  const unknown = new Set<string>();
  const presets = records.map((record) => {
    const entries: [string, unknown][] = [],
      targets = new Set<string>();
    for (const [key, value] of Object.entries(record.values)) {
      const matches = fields.filter(
        (field) => field.id === key || field.key === key,
      );
      if (!matches.length || matches[0].id === selectorId) {
        unknown.add(key);
        continue;
      }
      const target = presetTarget(fields, key);
      if (targets.has(target.id))
        throw new Error("PREPOPULATION_DUPLICATE_TARGET");
      targets.add(target.id);
      let typed = value;
      if (typeof value === "string") {
        if (
          ["image", "map"].includes(target.type) &&
          Object.hasOwn(assets, value)
        ) {
          if (!/^asset_[a-zA-Z0-9_-]+$/.test(assets[value]))
            throw new Error("PREPOPULATION_ASSET_INVALID");
          typed = assets[value];
        } else if (
          target.type === "number" &&
          value.trim() &&
          Number.isFinite(Number(value))
        )
          typed = Number(value);
        else if (
          target.type === "checkbox" &&
          !target.choices.length &&
          !isDynamicChoiceField(target) &&
          /^(true|false|1|0)$/i.test(value)
        )
          typed = /^(true|1)$/i.test(value);
        else if (
          ["image", "map", "address"].includes(target.type) ||
          (target.type === "checkbox" &&
            (target.choices.length || isDynamicChoiceField(target)))
        ) {
          try {
            const decoded = JSON.parse(value);
            if (decoded && typeof decoded === "object") typed = decoded;
          } catch {
            /* Plain legacy values stay plain. */
          }
        }
      }
      entries.push([target.id, typed]);
    }
    return { ...record, values: Object.fromEntries(entries) };
  });
  return { presets, unknown: [...unknown] };
}
const canonical = (value: unknown): string =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : item,
  );
export function serializePrepopulationXml(
  records: Records,
  fields: readonly Field[],
  original?: string,
  baseline?: Records,
): string {
  let previous: Records = [];
  if (original) {
    try {
      previous = parsePrepopulationXml(original);
    } catch {
      if (!records.length) return original;
    }
    const renamed = fields.some(
      (field) =>
        field.legacy?.PAGEFLEX_VARIABLE_NAME &&
        field.legacy.PAGEFLEX_VARIABLE_NAME !== field.key &&
        previous.some((record) =>
          Object.hasOwn(
            record.values,
            String(field.legacy!.PAGEFLEX_VARIABLE_NAME),
          ),
        ),
    );
    if (
      !renamed &&
      canonical(baseline || bindPrepopulation(previous, fields).presets) ===
        canonical(records)
    )
      return original;
  }
  const recognized = new Set(
    fields.flatMap((field) => [
      field.id,
      field.key,
      String(field.legacy?.PAGEFLEX_VARIABLE_NAME || ""),
    ]),
  );
  const rows = records.map((record) => {
    const old = previous.find((row) => row.id === record.id);
    const extra = Object.fromEntries(
      Object.entries(old?.values || {}).filter(([key]) => !recognized.has(key)),
    );
    const assigned: [string, string][] = [];
    for (const [key, value] of Object.entries(record.values)) {
      const target = presetTarget(fields, key);
      if (
        !/^[A-Za-z_][A-Za-z0-9_.-]*$/.test(target.key) ||
        ["value", "_pre_pop_id"].includes(target.key)
      )
        throw new Error("PREPOPULATION_TARGET_XML_NAME_INVALID");
      const before = baseline?.find((row) => row.id === record.id);
      const beforeValue =
        before &&
        Object.entries(before.values).find(
          ([key]) =>
            key === target.id ||
            key === target.key ||
            key === target.legacy?.PAGEFLEX_VARIABLE_NAME,
        );
      const oldColumn = [
        String(target.legacy?.PAGEFLEX_VARIABLE_NAME || ""),
        target.key,
      ].find((key) => old && Object.hasOwn(old.values, key));
      const flat =
        beforeValue &&
        oldColumn &&
        canonical(beforeValue[1]) === canonical(value)
          ? String(old!.values[oldColumn])
          : value === null
            ? ""
            : statementSelectionSchema.safeParse(value).success
              ? statementSelectionSchema.parse(value).text
              : typeof value === "object"
                ? JSON.stringify(value)
                : String(value);
      assigned.push([target.key, flat]);
    }
    return {
      _pre_pop_id: record.id,
      value: record.label,
      ...extra,
      ...Object.fromEntries(assigned),
    };
  });
  const xml = new XMLBuilder().build({
    prepopulatedataList: { prepopulatedata: rows },
  });
  // Apply the same bounds and identity checks to serialized output.
  parsePrepopulationXml(xml);
  return xml;
}
